import { ProductField, MappedField, IRsAllMastersData } from "../interfaces";
import { CONFIGURATION_KEYS, IMPORT_KEYS_VALIDATION } from "../constants/index";

interface ProductFieldWithMappedField extends ProductField {
  mappedfield: MappedField;
}

export const getConfigurationContext = (
  pathname: string,
  prevPath?: boolean,
  returnOption?: boolean
) => {
  if (prevPath) {
    pathname = pathname.substring(0, pathname.lastIndexOf("/"));
  }

  const option = pathname.slice(pathname.lastIndexOf("/"), pathname.length);
  if (returnOption) return option;

  switch (option) {
    case "/globalConfiguration":
      return prevPath ? "global" : undefined;
    case "/serverEndpoint":
      return "server";
    case "/fieldValidations":
      return "validations";
    default:
      return option.substring(1);
  }
};

export const getGlobalConfigurationPageLabel = (
  t: Function,
  pathname: string
) => {
  const pageType = pathname.slice(pathname.lastIndexOf("/"), pathname.length);

  switch (pageType) {
    case "/globalConfiguration":
      return t("Menu.TransversalLabel");
    case "/serverEndpoint":
      return t("Menu.EndpointLabel");
    case "/fieldValidations":
      return t("Menu.FieldValidationsLabel");
    case "/mobile":
      return t("Menu.MobileLabel");
    case "/iuvimarketing":
      return t("Menu.IuviMARKETINGLabel");
    case "/iuvinow":
      return t("Menu.IuviNOWLabel");
    case "/iuvisales":
      return t("Menu.IuviSALESLabel");
    case "/iuviprofiler":
      return t("Menu.IuviPROFILERLabel");
    case "/findmybank":
      return t("Menu.FindMyBankLabel");
    case "/administrative":
      return t("Menu.AdministrativeTransfersLabel");
    case "/security":
      return t("Menu.AutenticationAndSecurityLabel");
    default:
      return "";
  }
};

export const getContextFromKey = (key: string) => {
  const keyContext = key.substring(0, key.lastIndexOf("."));
  switch (keyContext) {
    case "default.global":
      return "globalConfiguration";
    case "default.server":
      return "serverEndpoint";
    case "default.validations":
      return "fieldValidations";
    case "default.iuviprofiler":
      return "iuviprofiler";
    case "default.iuvinow":
      return "iuvinow";
    case "default.iuvisales":
      return "iuvisales";
    case "default.iuvimarketing":
      return "iuvimarketing";
    case "default.mobile":
      return "mobile";
    case "default.findmybank":
      return "findmybank";
    case "default.administrative":
      return "administrative";
    case "default.security":
      return "security";
    default:
      return "";
  }
};

export const listOptions = (options: ProductFieldWithMappedField[]) => {
  let optionsObj = {};
  const types = [...Array.from(new Set(options.map((opt) => opt.usagetype)))];

  types.forEach((type) => {
    (optionsObj as any)[type] = options
      .filter((opt) => opt.usagetype === type)
      .map(({ fieldorder, mappedfield }) => ({
        label: mappedfield.labelkey,
        order: fieldorder,
        productItemTypeDefinition: mappedfield.dashboardoption,
        jsonPath: mappedfield.jsonpath,
        extraJsonPath: mappedfield.extrajsonpath,
        type: mappedfield.datatype,
      }));
  });

  return optionsObj;
};

export const securityKeyLabels = (key: string) => {
  return {
    label: "Security." + key + "Label",
    placeholder: "Security." + key + "Placeholder",
    tooltip: "Security." + key + "Tooltip",
  };
};

export const propertyKeyLabel = (t: Function, context: string, key: string) => {
  return t(context + key + "Label");
};

export const convertValueTypes = (value: string) => {
  if (value[0] !== "0" && value) {
    return value;
  } else if (["true", "false"].includes(`${value}`.toLowerCase())) {
    return value === "true";
  }
  return value;
};

export const importDataStructureConverter = (t: Function, data: any, isErrorImport?: boolean) => {
  const successData: any = {};
  const errorData: any = {};
  const successDataToShow: any[] = [];
  const errorDataToShow: any[] = [];
  if (!!data) {
    const currentKeys = Object.keys(data);

    const getConfigsInfo = (key: string, data: any, isSubItem: boolean) => {
      const successInfo: any[] = [];
      const errorInfo: any[] = [];

      const keysToValidate = Object.keys(CONFIGURATION_KEYS);
      if (isSubItem) {
        successData[key] = {
          configurations: [],
        };
        errorData[key] = {
          configurations: [],
        };
      } else {
        successData[key] = [];
        errorData[key] = [];
      }

      data?.forEach((item: any) => {
        const itemKeys = Object.keys(item);
        const isValid =
          itemKeys.every((key) => keysToValidate.includes(key)) &&
          itemKeys.length === keysToValidate.length;
        (isValid ? successInfo : errorInfo).push(item);
        (isValid ? successDataToShow : errorDataToShow).push({
          functionality: key,
          type: "configuration",
          name: item?.key?.split(".")?.pop(),
          status: isValid ? 'VALID' : 'INVALID',
          version: item?.releaseversion,
          [!isValid ? 'validations' : '']:
            !isValid && itemKeys.length < keysToValidate.length
              ? `${t("Environments.importValidationMessage1")} ${keysToValidate
                .filter((el) => !itemKeys.includes(el))
                .join(", ")} ${t("Environments.importValidationMessage3")}`
              : `${t("Environments.importValidationMessage1")} ${itemKeys
                .filter((el) => !keysToValidate.includes(el))
                .join(", ")} ${t("Environments.importValidationMessage2")}`,
          id: item?.id,
          createdat: item?.createdat,
          updatedat: item?.updatedat,
          [isErrorImport ? 'validations' : '']: item?.errorMessage,
        });
      });

      return {
        successInfo,
        errorInfo,
      };
    };

    currentKeys.forEach((key) => {
      const keyData = data[key];

      if (key === "widgets") {
        successData["widgets"] = [];
        errorData["widgets"] = [];
        const keysToValidate = Object.keys(IMPORT_KEYS_VALIDATION.widgets);
        keyData.forEach((item: any) => {
          const itemKeys = Object.keys(item);
          const isValid =
            itemKeys.every((key) => keysToValidate.includes(key)) &&
            itemKeys.length === keysToValidate.length;
          (isValid ? successData : errorData)["widgets"].push(item);
          (isValid ? successDataToShow : errorDataToShow).push({
            functionality: key,
            type: "widget",
            name: item?.widgetname,
            status: isValid ? 'VALID' : 'INVALID',
            version: item?.releaseversion,
            [!isValid ? 'validations' : '']:
              !isValid && itemKeys.length < keysToValidate.length
                ? `${t("Environments.importValidationMessage1")} ${keysToValidate
                  .filter((el) => !itemKeys.includes(el))
                  .join(", ")} ${t("Environments.importValidationMessage3")}`
                : `${t("Environments.importValidationMessage1")} ${itemKeys
                  .filter((el) => !keysToValidate.includes(el))
                  .join(", ")} ${t("Environments.importValidationMessage2")}`,
            id: item?.id,
            createdat: item?.createdat,
            updatedat: item?.updatedat,
            [isErrorImport ? 'validations' : '']: item?.errorMessage,
          });
        });
      } else if (key === "formatsandlanguages") {
        const keysToValidate = Object.keys(
          IMPORT_KEYS_VALIDATION.formatsandlanguages.languages
        );
        const { successInfo, errorInfo } = getConfigsInfo(
          key,
          keyData?.configurations,
          true
        );
        successData["formatsandlanguages"]["configurations"] = successInfo;
        errorData["formatsandlanguages"]["configurations"] = errorInfo;
        successData["formatsandlanguages"]["languages"] = [];
        errorData["formatsandlanguages"]["languages"] = [];
        keyData?.languages.forEach((item: any) => {
          const itemKeys = Object.keys(item);
          const isValid =
            itemKeys.every((key) => keysToValidate.includes(key)) &&
            itemKeys.length === keysToValidate.length;
          (isValid ? successData : errorData)["formatsandlanguages"][
            "languages"
          ].push(item);
          (isValid ? successDataToShow : errorDataToShow).push({
            functionality: key,
            type: "language",
            name: item?.isocode,
            status: isValid ? 'VALID' : 'INVALID',
            version: item?.releaseversion,
            [!isValid ? 'validations' : '']:
              !isValid && itemKeys.length < keysToValidate.length
                ? `${t("Environments.importValidationMessage1")} ${keysToValidate
                  .filter((el) => !itemKeys.includes(el))
                  .join(", ")} ${t("Environments.importValidationMessage3")}`
                : `${t("Environments.importValidationMessage1")} ${itemKeys
                  .filter((el) => !keysToValidate.includes(el))
                  .join(", ")} ${t("Environments.importValidationMessage2")}`,
            id: item?.id,
            createdat: item?.createdat,
            updatedat: item?.updatedat,
            [isErrorImport ? 'validations' : '']: item?.errorMessage,
          });
        });
      } else if (key === "iuvimarketing") {
        const keysToValidate = Object.keys(
          IMPORT_KEYS_VALIDATION.iuvimarketing.marketingcampaigns
        );
        const { successInfo, errorInfo } = getConfigsInfo(
          key,
          keyData?.configurations,
          true
        );
        successData["iuvimarketing"]["configurations"] = successInfo;
        errorData["iuvimarketing"]["configurations"] = errorInfo;
        successData["iuvimarketing"]["marketingcampaigns"] = [];
        errorData["iuvimarketing"]["marketingcampaigns"] = [];
        keyData?.marketingcampaigns.forEach((item: any) => {
          const itemKeys = Object.keys(item);
          const isValid =
            itemKeys.every((key) => keysToValidate.includes(key)) &&
            itemKeys.length === keysToValidate.length;
          (isValid ? successData : errorData)["iuvimarketing"][
            "marketingcampaigns"
          ].push(item);
          (isValid ? successDataToShow : errorDataToShow).push({
            functionality: key,
            type: "campaign",
            name: `${item?.type} - ${item?.advertiseid}`,
            status: isValid ? 'VALID' : 'INVALID',
            version: item?.releaseversion,
            [!isValid ? 'validations' : '']:
              !isValid && itemKeys.length < keysToValidate.length
                ? `${t("Environments.importValidationMessage1")} ${keysToValidate
                  .filter((el) => !itemKeys.includes(el))
                  .join(", ")} ${t("Environments.importValidationMessage3")}`
                : `${t("Environments.importValidationMessage1")} ${itemKeys
                  .filter((el) => !keysToValidate.includes(el))
                  .join(", ")} ${t("Environments.importValidationMessage2")}`,
            id: item?.id,
            createdat: item?.createdat,
            updatedat: item?.updatedat,
            [isErrorImport ? 'validations' : '']: item?.errorMessage,
          });
        });
      } else if (key === "translations") {
        const mainKeys: any = Object.keys(IMPORT_KEYS_VALIDATION.translations);
        const translationKeys = Object.keys(
          IMPORT_KEYS_VALIDATION.translations.translations
        );
        successData["translations"] = [];
        errorData["translations"] = [];
        keyData.forEach((item: any) => {
          const itemKeys = Object.keys(item);
          const isValidMain =
            Object.keys(item).every((key) => mainKeys.includes(key)) &&
            itemKeys.length === mainKeys.length;
          const isValidTranslation = item?.translations.every((trl: any) =>
            Object.keys(trl).every((key) => translationKeys.includes(key))
          );

          (isValidMain && isValidTranslation ? successData : errorData)[
            "translations"
          ].push(item);

          (isValidMain ? successDataToShow : errorDataToShow).push({
            functionality: key,
            type: "label",
            name: item?.key,
            status: isValidMain ? 'VALID' : 'INVALID',
            version: item?.releaseversion,
            [!isValidMain ? 'validations' : '']:
              !isValidMain && itemKeys.length < mainKeys.length
                ? `${t("Environments.importValidationMessage1")} ${mainKeys
                  .filter((el: any) => !itemKeys.includes(el))
                  .join(", ")} ${t("Environments.importValidationMessage3")}`
                : `${t("Environments.importValidationMessage1")} ${itemKeys
                  .filter((el: any) => !mainKeys.includes(el))
                  .join(", ")} ${t("Environments.importValidationMessage2")}`,
            id: item?.id,
            createdat: item?.createdat,
            updatedat: item?.updatedat,
            [isErrorImport ? 'validations' : '']: item?.errorMessage,
          });

          item?.translations.forEach((prop: any) => {
            const propKeys = Object.keys(prop);
            (isValidTranslation ? successDataToShow : errorDataToShow).push({
              functionality: key,
              type: "translation",
              name: `${item?.key} - ${prop?.value}`,
              status: isValidTranslation ? 'VALID' : 'INVALID',
              version: item?.releaseversion,
              [!isValidTranslation ? 'validations' : '']:
                !isValidTranslation && propKeys.length < translationKeys.length
                  ? `${t(
                    "Environments.importValidationMessage1"
                  )} ${translationKeys
                    .filter((el) => !propKeys.includes(el))
                    .join(", ")} ${t("Environments.importValidationMessage3")}`
                  : `${t("Environments.importValidationMessage1")} ${propKeys
                    .filter((el) => !translationKeys.includes(el))
                    .join(", ")} ${t("Environments.importValidationMessage2")}`,
              id: prop?.id,
              createdat: prop?.createdat,
              updatedat: prop?.updatedat,
              [isErrorImport ? 'validations' : '']: prop?.errorMessage,
            });
          });
        });
      } else if (["products", "transfers"].includes(key)) {
        let mainKeys: any, propKeys: any, fieldKeys: any;
        if (key === "products") {
          const { successInfo, errorInfo } = getConfigsInfo(
            key,
            keyData?.configurations,
            true
          );
          successData["products"] = {
            configurations: successInfo,
            information: [],
          };
          errorData["products"] = {
            configurations: errorInfo,
            information: [],
          };
          mainKeys = Object.keys(IMPORT_KEYS_VALIDATION.products.information);
          propKeys = Object.keys(
            IMPORT_KEYS_VALIDATION.products.information?.productproperties
          );
          fieldKeys = Object.keys(
            IMPORT_KEYS_VALIDATION.products.information?.productfields
          );
        } else {
          successData["transfers"] = [];
          errorData["transfers"] = [];
          mainKeys = Object.keys(IMPORT_KEYS_VALIDATION.transfers);
          propKeys = Object.keys(
            IMPORT_KEYS_VALIDATION.transfers?.transactionproperties
          );
          fieldKeys = Object.keys(
            IMPORT_KEYS_VALIDATION.transfers?.transactionfields
          );
        }

        (key === "products" ? keyData?.information : keyData).forEach(
          (item: any) => {
            const isValidMain = Object.keys(item).every((key) =>
              mainKeys.includes(key)
            );
            const areValidProps = item[
              key === "products" ? "productproperties" : "transactionproperties"
            ].every((prop: any) =>
              Object.keys(prop).every((key: any) => propKeys.includes(key))
            );
            const areValidFields = item[
              key === "products" ? "productfields" : "transactionfields"
            ].every((prop: any) =>
              Object.keys(prop).every((key: any) => fieldKeys.includes(key))
            );

            if (key === "products") {
              (isValidMain && areValidProps && areValidFields
                ? successData
                : errorData)["products"]["information"].push(item);
            } else {
              (isValidMain && areValidProps && areValidFields
                ? successData
                : errorData)["transfers"].push(item);
            }

            (isValidMain ? successDataToShow : errorDataToShow).push({
              functionality: key,
              type: "product",
              name: item?.name,
              status: isValidMain ? 'VALID' : 'INVALID',
              version: item?.releaseversion,
              [!isValidMain ? 'validations' : '']:
                !isValidMain &&
                `Las propiedades ${Object.keys(item)
                  .filter((el) => !mainKeys.includes(el))
                  .join(
                    ", "
                  )} no estan definidas para este tipo de configuración.`,
              id: item?.id,
              createdat: item?.createdat,
              updatedat: item?.updatedat,
              [isErrorImport ? 'validations' : '']: item?.errorMessage,
            });

            item[
              key === "products" ? "productproperties" : "transactionproperties"
            ].forEach((prop: any) => {
              const itemPropKeys = Object.keys(prop);
              (areValidProps ? successDataToShow : errorDataToShow).push({
                functionality: key,
                type: "property",
                name: prop?.relation,
                status: areValidProps ? 'VALID' : 'INVALID',
                version: prop?.releaseversion,
                [!areValidProps ? 'validations' : '']:
                  !areValidProps && itemPropKeys.length < propKeys.length
                    ? `${t("Environments.importValidationMessage1")} ${propKeys
                      .filter((el: any) => !itemPropKeys.includes(el))
                      .join(", ")} ${t(
                        "Environments.importValidationMessage3"
                      )}`
                    : `${t(
                      "Environments.importValidationMessage1"
                    )} ${itemPropKeys
                      .filter((el: any) => !propKeys.includes(el))
                      .join(", ")} ${t(
                        "Environments.importValidationMessage2"
                      )}`,
                id: prop?.id,
                createdat: prop?.createdat,
                updatedat: prop?.updatedat,
                [isErrorImport ? 'validations' : '']: prop?.errorMessage,
              });
            });

            item[
              key === "products" ? "productfields" : "transactionfields"
            ].forEach((field: any) => {
              const itemFieldKeys = Object.keys(field);
              (areValidFields ? successDataToShow : errorDataToShow).push({
                functionality: key,
                type: "field",
                name: field?.relation,
                status: areValidFields ? 'VALID' : 'INVALID',
                version: field?.releaseversion,
                [!areValidFields ? 'validations' : '']:
                  !areValidFields && itemFieldKeys.length < fieldKeys.length
                    ? `${t("Environments.importValidationMessage1")} ${fieldKeys
                      .filter((el: any) => !itemFieldKeys.includes(el))
                      .join(", ")} ${t(
                        "Environments.importValidationMessage3"
                      )}`
                    : `${t(
                      "Environments.importValidationMessage1"
                    )} ${itemFieldKeys
                      .filter((el: any) => !fieldKeys.includes(el))
                      .join(", ")} ${t(
                        "Environments.importValidationMessage2"
                      )}`,
                id: field?.id,
                createdat: field?.createdat,
                updatedat: field?.updatedat,
                [isErrorImport ? 'validations' : '']: field?.errorMessage,
              });
            });
          }
        );
      } else if (["productproperties", "transactionproperties"].includes(key)) {
        successData[key] = [];
        errorData[key] = [];

        const defaultPropsKeys = Object.keys(
          (IMPORT_KEYS_VALIDATION as any)?.[key]
        );

        keyData.forEach((item: any) => {
          const itemKeys = Object.keys(item);

          const isValidProp = Object.keys(item).every(
            (key: any) =>
              defaultPropsKeys.includes(key) &&
              itemKeys.length === defaultPropsKeys.length
          );

          (isValidProp ? successData : errorData)[key].push(item);
          (isValidProp ? successDataToShow : errorDataToShow).push({
            functionality: key,
            type: "property",
            name: item?.labelkey,
            status: isValidProp ? 'VALID' : 'INVALID',
            version: item?.releaseversion,
            [!isValidProp ? 'validations' : '']:
              !isValidProp && itemKeys.length !== defaultPropsKeys.length
                ? `${t(
                  "Environments.importValidationMessage1"
                )} ${defaultPropsKeys
                  .filter((el) => !itemKeys.includes(el))
                  .join(", ")} ${t("Environments.importValidationMessage3")}`
                : `${t("Environments.importValidationMessage1")} ${itemKeys
                  .filter((el) => !defaultPropsKeys.includes(el))
                  .join(", ")} ${t("Environments.importValidationMessage2")}`,
            id: item?.id,
            createdat: item?.createdat,
            updatedat: item?.updatedat,
            [isErrorImport ? 'validations' : '']: item?.errorMessage,
          });
        });
      } else if (["quicklinks", "mappedfields"].includes(key)) {
        const keysToValidate =
          key === "quicklinks"
            ? Object.keys(IMPORT_KEYS_VALIDATION.quicklinks)
            : Object.keys(IMPORT_KEYS_VALIDATION.mappedfields);
        successData[key] = [];
        errorData[key] = [];
        keyData?.forEach((item: any) => {
          const itemKeys = Object.keys(item);
          const isValid =
            itemKeys.every((key) => keysToValidate.includes(key)) &&
            Object.keys(item).length === keysToValidate.length;
          (isValid ? successData : errorData)[key].push(item);
          (isValid ? successDataToShow : errorDataToShow).push({
            functionality: key,
            type: `${key === "quicklinks" ? "quicklink" : "field"}`,
            name: item?.name,
            status: isValid ? 'VALID' : 'INVALID',
            version: item?.releaseversion,
            [!isValid ? 'validations' : '']:
              !isValid && itemKeys.length < keysToValidate.length
                ? `${t("Environments.importValidationMessage1")} ${keysToValidate
                  .filter((el) => !itemKeys.includes(el))
                  .join(", ")} ${t("Environments.importValidationMessage3")}`
                : `${t("Environments.importValidationMessage1")} ${itemKeys
                  .filter((el) => !keysToValidate.includes(el))
                  .join(", ")} ${t("Environments.importValidationMessage2")}`,
            id: item?.id,
            createdat: item?.createdat,
            updatedat: item?.updatedat,
            [isErrorImport ? 'validations' : '']: item?.errorMessage,
          });
        });
      } else {
        const { successInfo, errorInfo } = getConfigsInfo(key, keyData, false);
        successData[key] = successInfo;
        errorData[key] = errorInfo;
      }
    });
  };
    return {
      successData,
      errorData,
      successDataToShow,
      errorDataToShow,
    };
};

export const configsFilterToImport = (
  data: any,
  filters: {
    functionality: string[];
    version: string[];
  }
) => {
  const { functionality, version } = filters;

  if (functionality.length === 0 && version.length === 0) {
    return data;
  }

  const infoFilteredByFunctionality =
    functionality.length > 0
      ? Object.fromEntries(
        Object.entries(data).filter(([key, _]) => functionality.includes(key))
      )
      : data;

  if (functionality.length !== 0 && version.length === 0) {
    return infoFilteredByFunctionality;
  }

  const infoFilteredByVersion = Object.fromEntries(
    Object.entries(infoFilteredByFunctionality).map(([key, value]) => {
      if (["formatsandlanguages", "iuvimarketing"].includes(key)) {
        const keyRelations = {
          formatsandlanguages: "languages",
          iuvimarketing: "marketingcampaigns",
        };

        return [
          key,
          {
            configurations: (value as any)?.configurations.filter((item: any) =>
              version.includes(item?.releaseversion)
            ),
            [(keyRelations as any)[key]]: (value as any)?.[
              (keyRelations as any)[key]
            ].filter((item: any) => version.includes(item?.releaseversion)),
          },
        ];
      } else if (key === "translations") {
        return [
          key,
          (value as any).filter((item: any) =>
            version.includes(item?.releaseversion)
          ),
        ];
      } else if (["products", "transfers"].includes(key)) {
        if (key === "products") {
          return [
            key,
            {
              configurations: (value as any)?.configurations.filter(
                (item: any) => version.includes(item?.releaseversion)
              ),
              information: (value as any)?.information
                .map((item: any) => {
                  return {
                    ...item,
                    productproperties: item?.productproperties.filter(
                      (prop: any) => version.includes(prop?.releaseversion)
                    ),
                    productfields: item?.productfields.filter((field: any) =>
                      version.includes(field?.releaseversion)
                    ),
                  };
                })
                .filter(
                  (item: any) =>
                    item?.productproperties.length > 0 ||
                    item?.productfields.length > 0
                ),
            },
          ];
        } else {
          return [
            key,
            (value as any)
              .map((item: any) => ({
                ...item,
                transactionproperties: item?.transactionproperties.filter(
                  (prop: any) => version.includes(prop?.releaseversion)
                ),
                transactionfields: item?.transactionfields.filter((prop: any) =>
                  version.includes(prop?.releaseversion)
                ),
              }))
              .filter(
                (item: any) =>
                  item?.transactionproperties.length > 0 ||
                  item?.transactionfields.length > 0
              ),
          ];
        }
      } else {
        return [
          key,
          (value as any).filter((item: any) =>
            version.includes(item?.releaseversion)
          ),
        ];
      }
    })
  );

  return infoFilteredByVersion;
};

export const downloadInfo = (filename: string, info: any) => {
  const blob = new Blob([JSON.stringify(info)], {
    type: "application/json",
  });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.setAttribute("download", `${filename}.json`);
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

export const mastersFormatter = (rsAllMastersData: IRsAllMastersData[]) => {
  if (rsAllMastersData) {
    return rsAllMastersData.map((typeMaster) => ({ label: typeMaster.name, value: typeMaster.id }))
  }
  else return null
}

export const servicesFormatter = (data: any[]) => {
  if (data) {
    return data.map((typeService) => ({ label: typeService.name, value: typeService.id }))
  }
  else return null
}

export const pointOfAttentionFormatter = (pointOfAttentionData: any[], t: Function) => {
  if (pointOfAttentionData) {
    return pointOfAttentionData.map((data) => (
      {
        id: data?.id,
        code: data?.code,
        name: data?.name,
        pointServiceType: data?.pointServiceType.name,
        pointServiceTypeId: data?.pointServiceType.id,
        country: data?.country.name,
        countryId: data?.countryId,
        city: data?.city.name,
        cityId: data?.cityId,
        latitude: data?.latitude,
        longitude: data?.longitude,
        startOfValidity: data?.startDate,
        endOfValidity: data?.endDate,
        phone: data?.phone,
        extensionNumber: "0",
        observation: data?.observation,
        stateId: data?.stateId,
        department: data?.state?.name,
        address: data?.address,
        serviceIds: data?.services.map((service: any) => service.id),
        scheduleIds: data?.schedules.map((schedule: any) => schedule.id),
        layerName: data?.image?.layerName ? `${data?.image?.layerName}.${data?.image?.metadata?.extensionFile}` : '',
        layerSize: data?.image?.metadata?.size ? `${(data?.image?.metadata?.size / 1028).toFixed(2)} KB` : '',
        startDate: data?.startDate,
        endDate: data?.endDate,
        recordType: data?.recordType === "WITHOUT_DUE_DATE" ? t("PointsOfAttention.withoutDueDate") : t("PointsOfAttention.withDueDate"),
      }))
  }
  else return null
}

const formatDate = (value: any) => {
  return new Date(value).toLocaleDateString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export const filesFormatter = (filesFormatter: any[], t: Function) => {
  if (filesFormatter) {
    return filesFormatter.map((data: any) => (
      {
        id: data?.id,
        user: data?.createdBy,
        date: formatDate(data?.creationDate),
        registrationNumber: data?.totalRecords,
        latitude: "",
        stateProcess: data?.status,
        drafted: data?.resetRecord,
        urlOrigin: data?.urlOrigin,
        urlTransformed: data?.urlTransformed,
      }))
  }
  else return null
}

export const capitalizeFirstLetter = (data: any) => {
  const str = data.toLocaleLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const reportDate = (data: any) => {
  const optionsDate: any = { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
  const date = data.toLocaleDateString([], optionsDate);
  const dateFormat = date.split(",");
  const str1 = dateFormat[0].split('/');
  const str2 = dateFormat[1].split(":").join("").trim();

  const res = str1[2] + str1[1] + str1[0] + "-" + str2
  return res;
}

export const sassColorMixer = (color1: string | undefined, color2: string | undefined, percentage: number | undefined) => {
  if (color1 && color2 && percentage) {
    color1 = color1.split('#')[1];
    color2 = color2.split('#')[1];
    function d2h(d: any) { return d.toString(16); }  // convert a decimal value to hex
    function h2d(h: any) { return parseInt(h, 16); } // convert a hex value to decimal 

    percentage = (typeof (percentage) !== 'undefined') ? percentage : 50; // set the percentage to 50%, if that argument is omitted

    var color = "#";

    for (var i = 0; i <= 5; i += 2) { // loop through each of the 3 hex pairs—red, green, and blue
      var v1 = h2d(color1.substr(i, 2)), // extract the current pairs
        v2 = h2d(color2.substr(i, 2)),

        // combine the current pairs from each source color, according to the specified percentage
        val = d2h(Math.floor(v2 + (v1 - v2) * (percentage / 100.0)));

      while (val.length < 2) { val = '0' + val; } // prepend a '0' if val results in a single digit

      color += val; // concatenate val to our new color string
    }

    return color; // PROFIT!
  }
};

export const masterCategory = (data: any[], type: string) => {
  if (data) {
    return data
      .filter((item) => item.categoryId === type && (item.status === 'ACTIVE' || item.status === 'DRAFT'))
      .map((item) => ({ label: item.id, value: item.id }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }
  else return null
}
